(() => {
  const frameValues = new Set(['black', 'white', 'oak']);

  class LtpFrameGallerySync {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.mediaGallery = productInfo.querySelector('media-gallery');
      this.viewer = this.mediaGallery?.querySelector('[id^="GalleryViewer"]');
      if (!this.mediaGallery || !this.viewer) return;

      this.galleryDrivenMediaId = null;
      this.updateMedia = productInfo.updateMedia?.bind(productInfo);
      if (this.updateMedia) {
        productInfo.updateMedia = this.updateProductMedia.bind(this);
      }

      this.annotateGallery();
      this.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      this.activeMediaObserver = new MutationObserver((mutations) => {
        const activeMediaChanged = mutations.some(({ target, oldValue }) =>
          target.matches('[data-media-id]') &&
          (oldValue || '').split(/\s+/).includes('is-active') !== target.classList.contains('is-active')
        );
        if (activeMediaChanged) this.synchroniseActiveMedia();
      });
      this.activeMediaObserver.observe(this.mediaGallery.querySelector('[id^="Slider-Gallery"]'), {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
        subtree: true,
      });
      this.orderGallery();
    }

    onSlideChanged(event) {
      // resetPages also emits slideChanged while native media is being reordered.
      if (this.reconcilingGallery) return;
      const currentMedia = event.detail?.currentElement;
      if (!currentMedia) return;

      // Touch scrolling updates the slider page, but Dawn leaves .is-active behind.
      // Keep that marker current without calling setActiveMedia or moving the slider.
      if (!this.mediaGallery.mql.matches) {
        this.viewer.querySelectorAll('[data-media-id]').forEach((media) => {
          if (media.classList.contains('is-active') !== (media === currentMedia)) {
            media.classList.toggle('is-active', media === currentMedia);
          }
        });
      }
      this.synchroniseFrame(currentMedia);
    }

    synchroniseActiveMedia() {
      this.synchroniseFrame(this.mediaGallery.querySelector('[data-media-id].is-active'));
    }

    synchroniseFrame(activeMedia) {
      const frameValue = activeMedia?.dataset?.ltpFrameValue;
      if (!frameValues.has(frameValue)) return;

      const input = this.productInfo.querySelector(
        `variant-selects input[data-ltp-frame-value="${frameValue}"]`
      );
      if (!input || input.checked) return;

      this.galleryDrivenMediaId = activeMedia.dataset.mediaId;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      const selectedValue = input.closest('.product-form__input')?.querySelector('[data-selected-value]');
      if (selectedValue) selectedValue.textContent = input.value.charAt(0).toUpperCase() + input.value.slice(1);
    }

    updateProductMedia(html, variantFeaturedMediaId) {
      const requestedMediaId = variantFeaturedMediaId
        ? `${this.productInfo.dataset.section}-${variantFeaturedMediaId}`
        : null;

      if (requestedMediaId && requestedMediaId === this.galleryDrivenMediaId) {
        this.galleryDrivenMediaId = null;
        return;
      }

      this.galleryDrivenMediaId = null;
      this.reconcilingGallery = true;
      try {
        return this.updateMedia(html, variantFeaturedMediaId);
      } finally {
        this.reconcilingGallery = false;
      }
    }

    orderGallery() {
      this.annotateGallery();
      const activeMedia = this.mediaGallery.querySelector('[data-media-id].is-active');
      const reordered = this.sortChildren(
        this.mediaGallery.querySelector('[id^="Slider-Gallery"]'),
        '[data-ltp-gallery-order]'
      );

      const thumbnailList = this.mediaGallery.querySelector('[id^="Slider-Thumbnails"]');
      this.sortChildren(thumbnailList, '[data-ltp-gallery-order]');
      thumbnailList
        ?.querySelectorAll('[data-target]')
        .forEach((thumbnail, index) => {
          thumbnail.dataset.mediaPosition = index + 1;
        });

      this.mediaGallery.updateNavigationState?.();

      // A gallery-driven variant response must not restart a settled or ongoing swipe.
      if (!reordered) return;
      this.reconcilingGallery = true;
      try {
        this.viewer.resetPages?.();
      } finally {
        this.reconcilingGallery = false;
      }

      if (!activeMedia) return;
      requestAnimationFrame(() => {
        this.viewer.slider?.scrollTo({ left: activeMedia.offsetLeft });
      });
    }

    annotateGallery() {
      const mediaIds = {
        black: this.mediaGallery.dataset.ltpBlackMediaId,
        white: this.mediaGallery.dataset.ltpWhiteMediaId,
        oak: this.mediaGallery.dataset.ltpOakMediaId,
      };
      const mediaOrder = { black: 0, white: 1, oak: 2 };

      [
        ['[id^="Slider-Gallery"] > [data-media-id]', 'mediaId'],
        ['[id^="Slider-Thumbnails"] > [data-target]', 'target'],
      ].forEach(([selector, idKey]) => {
        let lifestyleOrder = 3;
        this.mediaGallery.querySelectorAll(selector).forEach((item) => {
          const frameValue = [...frameValues].find((value) => mediaIds[value] === item.dataset[idKey]);
          if (frameValue) {
            item.dataset.ltpFrameValue = frameValue;
            item.dataset.ltpGalleryOrder = mediaOrder[frameValue];
          } else {
            delete item.dataset.ltpFrameValue;
            item.dataset.ltpGalleryOrder = lifestyleOrder;
            lifestyleOrder += 1;
          }
        });
      });
    }

    sortChildren(container, selector) {
      if (!container) return false;
      const items = Array.from(container.querySelectorAll(`:scope > ${selector}`));
      const sortedItems = [...items].sort(
        (first, second) => Number(first.dataset.ltpGalleryOrder) - Number(second.dataset.ltpGalleryOrder)
      );
      if (items.every((item, index) => item === sortedItems[index])) return false;
      sortedItems.forEach((item) => container.append(item));
      return true;
    }
  }

  const initialise = (root = document) => {
    const productInfos = root.matches?.('product-info')
      ? [root]
      : root.querySelectorAll('product-info');

    productInfos.forEach((productInfo) => {
      if (productInfo.ltpFrameGallerySync) return;
      const mediaGallery = productInfo.querySelector('media-gallery');
      const galleryValues = [
        mediaGallery?.dataset.ltpBlackMediaId,
        mediaGallery?.dataset.ltpWhiteMediaId,
        mediaGallery?.dataset.ltpOakMediaId,
      ];
      const inputValues = Array.from(productInfo.querySelectorAll('input[data-ltp-frame-value]')).map(
        (input) => input.dataset.ltpFrameValue
      );
      const hasCompleteMapping =
        galleryValues.every(Boolean) && new Set(galleryValues).size === 3 && [...frameValues].every((value) => inputValues.includes(value));
      if (!hasCompleteMapping) return;

      productInfo.setAttribute('data-ltp-frame-sync', '');
      productInfo.ltpFrameGallerySync = new LtpFrameGallerySync(productInfo);
    });
  };

  document.addEventListener('product-info:loaded', (event) => initialise(event.target));
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = document.getElementById(`MainProduct-${data.sectionId}`);
      productInfo?.ltpFrameGallerySync?.orderGallery();
    });
  }

  initialise();
})();
